import { useState, useEffect, useRef, useCallback } from 'react';
import { OrderInfo, CostBreakdown, FinancialSummary, CostItem } from '../types/budget';
import { calculateFinancialSummary } from '../utils/calculations';

const initialOrderInfo: OrderInfo = {
  company: 'Shepherd Jeans LTD',
  date: new Date().toISOString().split('T')[0],
  buyer: 'Esquiss/Stock Italia',
  salesContract: '',
  style: '',
  po: '',
  item: '',
  orderQty: 0,
  expQty: 0,
  pricePerPc: 0,
  orderValue: 0,
  exportValue: 0,
  lcValue: 0,
};

const initialCostBreakdown: CostBreakdown = {
  yarn: [],
  knitting: [],
  dyeing: [],
  printing: [],
  aop: [],
  trimsAccessories: [],
  cm: [],
  commercial: [],
};

const loadSavedData = () => {
  try {
    const savedData = localStorage.getItem('garmentBudgetData');
    if (savedData) {
      const parsed = JSON.parse(savedData);
      console.log('Loading saved data:', parsed);
      console.log('Cost breakdown from storage:', JSON.stringify(parsed.costBreakdown, null, 2));
      return {
        orderInfo: { ...initialOrderInfo, ...parsed.orderInfo },
        costBreakdown: {
          yarn: parsed.costBreakdown?.yarn || [],
          knitting: parsed.costBreakdown?.knitting || [],
          dyeing: parsed.costBreakdown?.dyeing || [],
          printing: parsed.costBreakdown?.printing || [],
          aop: parsed.costBreakdown?.aop || [],
          trimsAccessories: parsed.costBreakdown?.trimsAccessories || [],
          cm: parsed.costBreakdown?.cm || [],
          commercial: parsed.costBreakdown?.commercial || [],
        },
      };
    }
  } catch (error) {
    console.error('Error loading saved data:', error);
  }
  return { orderInfo: initialOrderInfo, costBreakdown: initialCostBreakdown };
};

export const useBudgetData = () => {
  const savedData = loadSavedData();
  
  // Debounced save function to prevent excessive localStorage writes
  const debouncedSave = useRef<NodeJS.Timeout | null>(null);

  const saveToLocalStorage = useCallback((orderInfo: OrderInfo, costBreakdown: CostBreakdown) => {
    if (debouncedSave.current) {
      clearTimeout(debouncedSave.current);
    }
    
    debouncedSave.current = setTimeout(() => {
      try {
        const dataToSave = {
          orderInfo,
          costBreakdown,
          timestamp: new Date().toISOString(),
        };
        localStorage.setItem('garmentBudgetData', JSON.stringify(dataToSave));
        console.log('Data saved to localStorage:', dataToSave);
        console.log('Cost breakdown saved:', JSON.stringify(dataToSave.costBreakdown, null, 2));
      } catch (error) {
        console.error('Error saving data:', error);
      }
    }, 200);
  }, []);

  const [tabValue, setTabValue] = useState(0);
  const [orderInfo, setOrderInfo] = useState<OrderInfo>(savedData.orderInfo);
  const [costBreakdown, setCostBreakdown] = useState<CostBreakdown>(savedData.costBreakdown);
  const [financialSummary, setFinancialSummary] = useState<FinancialSummary>({
    totalCost: 0,
    totalCostPercentage: 0,
    profit: 0,
    profitPercentage: 0,
    finalCmPerDz: 0,
    finalMcEarning: 0,
    contributionMarginPerUnit: 0,
    breakevenUnits: 0,
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [showNewPlanDialog, setShowNewPlanDialog] = useState(false);

  // Save data to localStorage whenever it changes (with debounce)
  useEffect(() => {
    saveToLocalStorage(orderInfo, costBreakdown);
  }, [orderInfo, costBreakdown, saveToLocalStorage]);

  // Calculate financial summary whenever order info or cost breakdown changes
  useEffect(() => {
    const allCostItems = Object.values(costBreakdown).flat();
    const summary = calculateFinancialSummary(orderInfo, allCostItems);
    setFinancialSummary(summary);
  }, [orderInfo, costBreakdown]);

  // Debug costBreakdown changes
  useEffect(() => {
    console.log('Cost breakdown state updated:', costBreakdown);
    console.log('Knitting items:', costBreakdown.knitting);
    console.log('Dyeing items:', costBreakdown.dyeing);
  }, [costBreakdown]);

  // Cleanup function to save data when component unmounts
  useEffect(() => {
    return () => {
      if (debouncedSave.current) {
        clearTimeout(debouncedSave.current);
      }
      try {
        const dataToSave = {
          orderInfo,
          costBreakdown,
          timestamp: new Date().toISOString(),
        };
        localStorage.setItem('garmentBudgetData', JSON.stringify(dataToSave));
      } catch (error) {
        console.error('Error saving data on unmount:', error);
      }
    };
  }, [orderInfo, costBreakdown]);

  const handleOrderInfoChange = (field: keyof OrderInfo, value: any) => {
    setOrderInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleCostBreakdownChange = (category: keyof CostBreakdown, items: CostItem[]) => {
    console.log(`Cost breakdown change for ${category}:`, items);
    setCostBreakdown(prev => ({ ...prev, [category]: items }));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSave = () => {
    if (debouncedSave.current) {
      clearTimeout(debouncedSave.current);
      debouncedSave.current = null;
    }
    
    try {
      const dataToSave = {
        orderInfo,
        costBreakdown,
        timestamp: new Date().toISOString(),
      };
      
      localStorage.setItem('garmentBudgetData', JSON.stringify(dataToSave));
      
      const savedData = localStorage.getItem('garmentBudgetData');
      const parsedSavedData = savedData ? JSON.parse(savedData) : null;
      
      console.log('Data immediately saved to localStorage:', dataToSave);
      console.log('Cost breakdown in immediate save:', JSON.stringify(dataToSave.costBreakdown, null, 2));
      console.log('Verification - data read back from localStorage:', parsedSavedData);
      
      if (parsedSavedData && JSON.stringify(parsedSavedData.costBreakdown) === JSON.stringify(dataToSave.costBreakdown)) {
        console.log('✅ Save verification successful - data matches');
        setShowSuccess(true);
      } else {
        console.error('❌ Save verification failed - data mismatch');
        alert('Save verification failed. Please try again.');
      }
    } catch (error) {
      console.error('Error saving data:', error);
      alert('Error saving data: ' + (error as Error).message);
    }
  };

  const handleNewPlan = () => {
    setShowNewPlanDialog(true);
  };

  const confirmNewPlan = () => {
    localStorage.removeItem('garmentBudgetData');
    
    setOrderInfo(initialOrderInfo);
    setCostBreakdown(initialCostBreakdown);
    setFinancialSummary({
      totalCost: 0,
      totalCostPercentage: 0,
      profit: 0,
      profitPercentage: 0,
      finalCmPerDz: 0,
      finalMcEarning: 0,
      contributionMarginPerUnit: 0,
      breakevenUnits: 0,
    });
    
    setShowNewPlanDialog(false);
    console.log('New plan created - all data cleared');
    setShowSuccess(true);
  };

  const handleRefreshLocalStorage = () => {
    const savedData = localStorage.getItem('garmentBudgetData');
    if (savedData) {
      const parsed = JSON.parse(savedData);
      console.log('Manual refresh - loading from localStorage:', parsed);
      setOrderInfo({ ...initialOrderInfo, ...parsed.orderInfo });
      setCostBreakdown({
        yarn: parsed.costBreakdown?.yarn || [],
        knitting: parsed.costBreakdown?.knitting || [],
        dyeing: parsed.costBreakdown?.dyeing || [],
        printing: parsed.costBreakdown?.printing || [],
        aop: parsed.costBreakdown?.aop || [],
        trimsAccessories: parsed.costBreakdown?.trimsAccessories || [],
        cm: parsed.costBreakdown?.cm || [],
        commercial: parsed.costBreakdown?.commercial || [],
      });
      alert('Data refreshed from localStorage');
    } else {
      alert('No saved data found in localStorage');
    }
  };

  return {
    // State
    tabValue,
    orderInfo,
    costBreakdown,
    financialSummary,
    showSuccess,
    showNewPlanDialog,
    
    // Handlers
    handleOrderInfoChange,
    handleCostBreakdownChange,
    handleTabChange,
    handleSave,
    handleNewPlan,
    confirmNewPlan,
    handleRefreshLocalStorage,
    setShowSuccess,
    setShowNewPlanDialog,
  };
};
